import { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle, XCircle, QrCode, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InvoiceQRModal } from '@/components/invoice/InvoiceQRModal';
import { CreateInvoicePanel } from '@/components/invoice/CreateInvoicePanel';
// import { useInvoicePaymentMonitor } from '@/hooks/usePaymentMonitor';
import { useInvoiceContract } from '@/hooks/useInvoiceContract';
import { Invoice as InvoiceType } from '@/types/invoice';
import { useCountdown } from '@/hooks/useCountdown';
import toast from 'react-hot-toast';

interface Invoice {
  id: string;
  clientName: string;
  clientCode: string;
  details: string;
  amount: number;
  currency: string;
  musdAmount: number;
  status: 'pending' | 'paid' | 'cancelled' | 'draft' | 'expired';
  createdAt: string;
  wallet: string;
  bitcoinAddress?: string;
  expiresAt?: string;
}

interface InvoicesProps {
  invoices: InvoiceType[];
}

export function Invoices({ invoices }: InvoicesProps) {
  const { confirmPayment, cancelInvoice, refreshData } = useInvoiceContract();
  
  // Debug: Log invoices prop changes
  useEffect(() => {
    console.log('📦 Invoices component received invoices:', invoices.length);
    if (invoices.length > 0) {
      console.log('📦 Invoice IDs in component:', invoices.map(inv => inv.id));
      console.log('📦 Invoice details in component:', invoices.map(inv => ({
        id: inv.id,
        clientName: inv.clientName,
        status: inv.status,
        createdAt: inv.createdAt
      })));
    }
  }, [invoices]);
  
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrInvoice, setQrInvoice] = useState<Invoice | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid' | 'cancelled' | 'expired'>('all');
  const [bitcoinPrice, setBitcoinPrice] = useState<number>(0);
  const [_isLoadingPrice, _setIsLoadingPrice] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPrice = async () => {
      try {
        _setIsLoadingPrice(true);
        const { fetchBitcoinPrice } = await import('@/lib/bitcoin-price');
        const price = await fetchBitcoinPrice();
        if (!cancelled) setBitcoinPrice(price || 0);
      } catch {
        if (!cancelled) setBitcoinPrice(0);
      } finally {
        if (!cancelled) _setIsLoadingPrice(false);
      }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Compute derived status for expiry and filter accordingly
  const filteredInvoices = useMemo(() => {
    console.log('🔍 Filtering invoices:', {
      totalInvoices: invoices.length,
      statusFilter,
      invoiceStatuses: invoices.map(inv => ({ id: inv.id, status: inv.status, expiresAt: inv.expiresAt }))
    });
    
    const now = Date.now();
    const withDerived = invoices.map(inv => {
      const isExpired = inv.expiresAt ? new Date(inv.expiresAt).getTime() <= now : false;
      const derivedStatus = inv.status === 'pending' && isExpired ? 'expired' : inv.status;
      return { ...inv, status: derivedStatus } as InvoiceType & { status: 'pending' | 'paid' | 'cancelled' | 'expired' };
    });
    
    console.log('🔍 After deriving status:', {
      withDerived: withDerived.map(inv => ({ id: inv.id, status: inv.status }))
    });
    
    if (statusFilter === 'all') {
      console.log('✅ Returning all invoices:', withDerived.length);
      return withDerived;
    }
    
    const filtered = withDerived.filter(inv => inv.status === statusFilter);
    console.log(`✅ Filtered by ${statusFilter}:`, filtered.length, 'invoices');
    console.log('🔍 Filtered invoice details:', filtered.map(inv => ({ id: inv.id, status: inv.status, clientName: inv.clientName })));
    return filtered;
  }, [invoices, statusFilter, refreshTrigger]);

  // Periodically refresh to update expired status (every second)
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 1000); // Check every second for expired invoices

    return () => clearInterval(interval);
  }, []);

  const handleMarkAsPaidClick = async () => {
    if (!selectedInvoice) return;
    
    // VERIFICATION: Log invoice data to verify on-chain status
    console.log('🚨 MARK AS PAID CLICKED - Invoice Verification:', {
      id: selectedInvoice.id,
      idType: typeof selectedInvoice.id,
      isNumeric: !isNaN(Number(selectedInvoice.id)) && !isNaN(parseInt(selectedInvoice.id, 10)),
      startsWithDraft: String(selectedInvoice.id).startsWith('draft_'),
      startsWithPending: String(selectedInvoice.id).startsWith('pending_'),
      txHash: selectedInvoice.txHash,
      hasTxHash: !!selectedInvoice.txHash,
      status: selectedInvoice.status,
      clientCode: selectedInvoice.clientCode,
      fullInvoice: selectedInvoice
    });
    
    toast.loading('Checking payment...', { id: 'verify-payment' });
    try {
      await confirmPayment(selectedInvoice.id);
      toast.dismiss('verify-payment');
      // Don't show success here - confirmPayment handles its own toasts
      // Only close modal after wallet confirms and confirmPayment completes successfully
      setSelectedInvoice(null);
      refreshData();
    } catch (error) {
      toast.dismiss('verify-payment');
      console.error('Error marking as paid:', error);
      // Error toast is handled by confirmPayment
      // Don't close modal on error - let user see the error
    }
  };

  const handleCancelClick = async () => {
    if (!selectedInvoice) return;
    
    // VERIFICATION: Log invoice data to verify on-chain status
    console.log('🚨 CANCEL CLICKED - Invoice Verification:', {
      id: selectedInvoice.id,
      idType: typeof selectedInvoice.id,
      isNumeric: !isNaN(Number(selectedInvoice.id)) && !isNaN(parseInt(selectedInvoice.id, 10)),
      startsWithDraft: String(selectedInvoice.id).startsWith('draft_'),
      startsWithPending: String(selectedInvoice.id).startsWith('pending_'),
      txHash: selectedInvoice.txHash,
      hasTxHash: !!selectedInvoice.txHash,
      status: selectedInvoice.status,
      clientCode: selectedInvoice.clientCode,
      fullInvoice: selectedInvoice
    });
    
    try {
      // cancelInvoice will handle wallet popup and all toasts
      // Don't close modal or show success here - let cancelInvoice handle it
      await cancelInvoice(selectedInvoice.id);
      // Only close modal after wallet confirms and cancelInvoice completes successfully
      setSelectedInvoice(null);
      refreshData();
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      // Error toast is handled by cancelInvoice
      // Don't close modal on error - let user see the error
    }
  };

  const handleShowQR = (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setQrInvoice(invoice);
    setShowQRModal(true);
  };

  const handleCopyAddress = async (address: string) => {
    if (!address.trim()) {
      toast.error('No address to copy');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      toast.success('Address copied to clipboard!');
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (error) {
      console.error('Failed to copy address:', error);
      toast.error('Failed to copy address');
    }
  };

  

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <i className="fa-solid fa-file-invoice text-white text-2xl sm:text-3xl"></i>
            <h1 className="text-2xl sm:text-3xl font-bold font-navbar text-white">Invoices</h1>
          </div>
          <p className="text-sm font-navbar text-white/60">
            {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''} 
            {statusFilter !== 'all' && ` (${statusFilter})`}
          </p>
        </div>
      </div>

      {/* Status Filter */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {(['all', 'pending', 'paid', 'cancelled', 'expired'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "px-3 sm:px-4 py-2.5 sm:py-2 rounded-full text-xs sm:text-sm font-navbar font-medium transition-all capitalize border min-h-[44px] touch-manipulation active:scale-95",
                statusFilter === status
                  ? "bg-green-500/10 text-green-400 border-green-400/30"
                  : "bg-[#2C2C2E]/60 text-white/60 border-transparent hover:bg-green-500/5 hover:border-green-400/10 active:bg-green-500/10"
              )}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Create Invoice Button */}
      <div className="mb-6 flex justify-center">
        <CreateInvoicePanel />
      </div>

      {/* Invoices Grid */}
      <div className="space-y-4">
        {filteredInvoices.length === 0 && invoices.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-400/20 rounded-lg p-4 mb-4">
            <p className="text-sm font-navbar text-yellow-400">
              ⚠️ Debug: You have {invoices.length} invoice(s) but {filteredInvoices.length} match the filter "{statusFilter}".
              <br />
              Invoice statuses: {[...new Set(invoices.map(inv => inv.status))].join(', ')}
            </p>
          </div>
        )}
        {filteredInvoices.map((invoice) => {
          // Use countdown timer for pending invoices
          const InvoiceCardWithTimer = () => {
            const countdown = invoice.expiresAt && invoice.status === 'pending' 
              ? useCountdown(invoice.expiresAt)
              : null;
            // Use localStorage to persist expiry notification state across re-renders
            const expiryNotificationKey = `invoice-expired-notified-${invoice.id}`;
            const [hasNotifiedExpiry, setHasNotifiedExpiry] = useState(() => {
              if (typeof window !== 'undefined') {
                return localStorage.getItem(expiryNotificationKey) === 'true';
              }
              return false;
            });

            // Auto-update status to expired if countdown expired
            useEffect(() => {
              if (countdown?.isExpired && invoice.status === 'pending' && !hasNotifiedExpiry) {
                // Trigger refresh to update status
                setRefreshTrigger(prev => prev + 1);
                // Send notification for expired invoice
                try {
                  window.dispatchEvent(new CustomEvent('notify', { 
                    detail: { 
                      title: 'Invoice expired', 
                      message: `Invoice for ${invoice.clientName} has expired`,
                      key: `invoice-expired-${invoice.id}`
                    } 
                  }));
                  console.log('✅ Invoice expired notification sent for invoice:', invoice.id);
                } catch (error) {
                  console.error('❌ Failed to send expired invoice notification:', error);
                }
                // Persist notification state to localStorage
                if (typeof window !== 'undefined') {
                  localStorage.setItem(expiryNotificationKey, 'true');
                }
                setHasNotifiedExpiry(true);
              }
            }, [countdown?.isExpired, invoice.status, invoice.clientName, hasNotifiedExpiry, invoice.id, expiryNotificationKey]);

            return (
              <div
                className="bg-[#2C2C2E]/40 backdrop-blur-xl border border-green-400/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:border-green-400/20 transition-all group active:scale-[0.99] touch-manipulation"
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2 group-hover:text-white transition-colors font-navbar text-white truncate">
                  {invoice.clientName}
                </h3>
                <p className="text-xs font-navbar text-white/50 mb-1 sm:mb-2 truncate">{invoice.clientCode}</p>
                <p className="text-xs sm:text-sm font-navbar text-white/60 line-clamp-2">{invoice.details}</p>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 ml-2 shrink-0">
                <button
                  onClick={(e) => handleShowQR(invoice, e)}
                  className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg border border-green-400/30 bg-green-500/10 hover:bg-green-500/20 active:bg-green-500/30 transition-all flex items-center justify-center group touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
                  title="Share QR Code"
                >
                  <QrCode className="w-4 h-4 text-white" />
                </button>
                {invoice.status === 'pending' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedInvoice(invoice);
                    }}
                    className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg border border-red-400/30 bg-red-500/10 hover:bg-red-500/20 active:bg-red-500/30 transition-all flex items-center justify-center touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0"
                    title="Cancel Invoice"
                  >
                    <XCircle className="w-4 h-4 text-white" />
                  </button>
                )}
                <span
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-navbar font-medium border",
                    invoice.status === 'paid' && "bg-green-500/10 text-green-400 border-green-400/20",
                    invoice.status === 'pending' && "bg-yellow-500/10 text-yellow-400 border-yellow-400/20",
                    invoice.status === 'cancelled' && "bg-red-500/10 text-red-400 border-red-400/20",
                    invoice.status === 'expired' && "bg-gray-500/10 text-gray-400 border-gray-400/20"
                  )}
                >
                    {invoice.status}
                  </span>
                </div>
              </div>
              
              {/* Payment Methods Badge */}
              {(invoice.paymentMethod === 'both' || invoice.lightningInvoice || invoice.paymentMethod === 'lightning') && (
                <div className="mb-2 flex items-center gap-2 flex-wrap">
                  {invoice.bitcoinAddress || invoice.payToAddress ? (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium font-navbar bg-green-500/10 text-green-400 border border-green-400/20">
                      ₿ Bitcoin
                    </span>
                  ) : null}
                  {invoice.lightningInvoice && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium font-navbar bg-yellow-500/10 text-yellow-400 border border-yellow-400/20">
                      ⚡ Lightning
                    </span>
                  )}
                </div>
              )}

              {/* Timer Display for Pending Invoices - Simple Escrow Style */}
              {invoice.status === 'pending' && invoice.expiresAt && (
                <div className="mb-4 pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-xs font-navbar text-white/50">Expires in</span>
                    </div>
                    <span className={cn(
                      "text-sm font-mono font-medium tabular-nums",
                      countdown?.isExpired 
                        ? "text-red-400"
                        : countdown && countdown.hours === 0 && countdown.minutes < 5
                        ? "text-red-400"
                        : "text-white/70"
                    )}>
                      {countdown ? (
                        countdown.isExpired 
                          ? 'Expired' 
                          : countdown.hours > 0
                          ? `${countdown.hours}:${countdown.minutes.toString().padStart(2, '0')}:${countdown.seconds.toString().padStart(2, '0')}`
                          : `${countdown.minutes}:${countdown.seconds.toString().padStart(2, '0')}`
                      ) : '--:--'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Expired Indicator */}
              {invoice.status === 'expired' && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-400/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-navbar text-red-400">Invoice expired</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between mb-4">
              <div>
                {(() => {
                  // Check if amount exists and is valid
                  const hasValidAmount = invoice.amount !== undefined && invoice.amount !== null && !isNaN(invoice.amount) && invoice.amount > 0;
                  
                  if (hasValidAmount) {
                    return (
                      <>
                        <p className="text-2xl font-bold font-navbar mb-1 text-white">
                          {invoice.currency === 'USD' || invoice.currency === 'KES' 
                            ? (bitcoinPrice > 0 
                                ? `$${(invoice.amount * bitcoinPrice).toFixed(2)}`
                                : `${invoice.amount.toFixed(8)} BTC`)
                            : `${invoice.amount.toFixed(8)} BTC`}
                        </p>
                        <p className="text-sm font-navbar text-white/60">
                          {invoice.currency === 'USD' || invoice.currency === 'KES'
                            ? `${invoice.amount.toFixed(8)} BTC`
                            : (bitcoinPrice > 0 
                                ? `$${(invoice.amount * bitcoinPrice).toFixed(2)} USD`
                                : 'Loading price...')}
                        </p>
                      </>
                    );
                  } else {
                    // Log for debugging
                    console.warn('⚠️ Invoice has invalid amount:', {
                      invoiceId: invoice.id,
                      amount: invoice.amount,
                      amountType: typeof invoice.amount,
                      currency: invoice.currency
                    });
                    return (
                      <div>
                        <p className="text-lg font-navbar text-yellow-400 mb-1">Amount loading...</p>
                        <p className="text-xs font-navbar text-white/40">Waiting for blockchain data</p>
                      </div>
                    );
                  }
                })()}
              </div>
              <div className="text-right">
                <p className="text-xs font-navbar text-white/50 mb-1">Created</p>
                <p className="text-sm font-navbar text-white/70">{new Date(invoice.createdAt).toLocaleString()}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-green-400/10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                <span className="text-xs font-navbar text-white/60">
                  Mezo: {invoice.bitcoinAddress ? `${invoice.bitcoinAddress.slice(0, 8)}...${invoice.bitcoinAddress.slice(-8)}` : 'Not set'}
                </span>
              </div>
              <button
                onClick={() => setSelectedInvoice(invoice)}
                className="px-4 py-2.5 sm:py-2 rounded-full bg-green-500/10 border border-green-400/30 hover:bg-green-500/20 hover:border-green-400/50 active:bg-green-500/30 transition-all text-xs sm:text-sm font-navbar font-medium text-white min-h-[44px] touch-manipulation active:scale-95 w-full sm:w-auto"
              >
                View Details
              </button>
              </div>
            </div>
            );
          };

          return <InvoiceCardWithTimer key={invoice.id} />;
        })}

        {filteredInvoices.length === 0 && (
          <div className="bg-[#2C2C2E]/40 backdrop-blur-xl border border-green-400/10 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-400/30 flex items-center justify-center mx-auto mb-4">
              <QrCode className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2 font-navbar text-white">No invoices yet</h3>
            <p className="text-white/60 text-sm mb-2 font-navbar">
              {statusFilter === 'all' 
                ? 'Create your first invoice to get started'
                : `No ${statusFilter} invoices found`
              }
            </p>
            {statusFilter === 'all' && (
              <p className="text-xs font-navbar text-white/40">Click "Create Invoice" above to get started</p>
            )}
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 safe-area-inset">
          <div className="bg-[#2C2C2E]/90 backdrop-blur-xl border border-green-400/20 p-4 sm:p-6 rounded-xl sm:rounded-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="flex items-center gap-2 text-xl font-semibold font-navbar text-white">
                <span className="text-[#F7931A] text-sm">₿</span>
                Invoice Details
              </h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Invoice Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Client Name</label>
                  <p className="text-white">{selectedInvoice.clientName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Client Code</label>
                  <p className="text-white">{selectedInvoice.clientCode}</p>
                </div>
                {(selectedInvoice.currency === 'USD' || selectedInvoice.currency === 'KES') ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Amount</label>
                      <p className="text-white">
                        {bitcoinPrice > 0 
                          ? `$${(selectedInvoice.amount * bitcoinPrice).toFixed(2)}`
                          : `$${selectedInvoice.amount.toFixed(2)} (price loading...)`}
                        {selectedInvoice.currency === 'KES' ? ' KES' : ' USD'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Bitcoin Equivalent</label>
                      <p className="text-white">{selectedInvoice.amount.toFixed(8)} BTC</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">Amount</label>
                      <p className="text-white">{selectedInvoice.amount.toFixed(8)} BTC</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">USD Equivalent</label>
                      <p className="text-white">
                        {bitcoinPrice > 0 
                          ? `$${(selectedInvoice.amount * bitcoinPrice).toFixed(2)} USD`
                          : 'Loading price...'}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Status</label>
                  <span
                    className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium",
                      selectedInvoice.status === 'paid' && "bg-green-500/20 text-green-400",
                      selectedInvoice.status === 'pending' && "bg-yellow-500/20 text-yellow-400",
                      selectedInvoice.status === 'cancelled' && "bg-red-500/20 text-red-400"
                    )}
                  >
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Created</label>
                  <p className="text-white">{new Date(selectedInvoice.createdAt).toLocaleString()}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Description</label>
                <p className="text-white">{selectedInvoice.details}</p>
              </div>

              {/* Receiving address */}
              {(selectedInvoice as any).payToAddress || selectedInvoice.bitcoinAddress ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-sm font-medium text-white/80">Receiving address</label>
                    {selectedInvoice.status === 'paid' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-400/30 font-navbar">auto-detected</span>
                    )}
                  </div>
                  <div className="relative p-3 bg-white/5 rounded-xl">
                    <p className="text-white font-mono text-sm break-all pr-10">{(selectedInvoice as any).payToAddress || selectedInvoice.bitcoinAddress}</p>
                    <button
                      onClick={() => handleCopyAddress((selectedInvoice as any).payToAddress || selectedInvoice.bitcoinAddress || '')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/60 hover:text-green-400 hover:bg-white/10 rounded-lg transition-all duration-200 active:scale-95"
                      title="Copy address"
                    >
                      {copiedAddress === ((selectedInvoice as any).payToAddress || selectedInvoice.bitcoinAddress) ? (
                        <i className="fa-solid fa-check text-green-400"></i>
                      ) : (
                        <i className="fa-solid fa-copy"></i>
                      )}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Dev payment debug removed for cleaner UX */}

              {/* Actions */}
              {selectedInvoice.status === 'pending' && selectedInvoice.expiresAt && new Date(selectedInvoice.expiresAt).getTime() > Date.now() && (
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <Button
                    onClick={handleMarkAsPaidClick}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 active:from-green-700 active:to-green-800 flex items-center gap-2 min-h-[44px] touch-manipulation active:scale-95"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm sm:text-base">Mark as Paid</span>
                  </Button>
                  <Button
                    onClick={handleCancelClick}
                    variant="outline"
                    className="flex-1 flex items-center gap-2 min-h-[44px] touch-manipulation active:scale-95"
                  >
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm sm:text-base">Cancel Invoice</span>
                  </Button>
                </div>
              )}
              {/* Approval step removed; paid is final for UX simplicity */}
            </div>
          </div>
        </div>
      )}


      {/* QR Modal */}
      <InvoiceQRModal 
        invoice={qrInvoice as InvoiceType}
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
      />

      
    </div>
  );
}

// InvoiceTimer removed (unused)